/**
 * ConnectionCheck — "is my VPN on, and does anything go around it?"
 *
 * One owner for the question that /vpn-checker, /privacy-audit,
 * /webrtc-leak-test and /ip-lookup each used to answer on their own — and
 * each answered wrong in its own way (26.09.2026, feedback from a VPN user on
 * /privacy-audit: "I am using a VPN but it tells me the VPN is off, and it
 * shows the VPN's location, not mine"):
 *   • /privacy-audit printed "No VPN detected — your real IP is visible" to
 *     EVERY visitor: its check was a constant;
 *   • /vpn-checker trusted `proxy||hosting` flags that no provider we may use
 *     fills in, so since 13.08 it could not say "VPN" to anyone — 0 green
 *     verdicts in 7 276 runs over 30 days, 93 % red "VPN is off or leaking";
 *   • every WebRTC test called ANY public address a leak — including the
 *     VPN's own, which is exactly what a working VPN shows (86 % of runs).
 *
 * What is compared, and why each piece is needed:
 *   you      — the address THIS site sees (our server, /api/connection-check);
 *   echoes   — what OTHER sites see, per address family (ipify / ident.me):
 *              a VPN that tunnels IPv4 but lets IPv6 out directly, or one that
 *              routes some sites around the tunnel (split tunnelling — common
 *              for Russian sites, and timbrica.com is one), only shows up when
 *              two vantage points disagree;
 *   webrtc   — addresses the browser hands out over WebRTC (STUN over UDP):
 *              a proxy-style "VPN" that carries only web traffic leaks the
 *              real address here;
 *   device   — the clock's time zone: a second, independent signal when the
 *              exit network is not recognised as a data centre;
 *   dns      — which resolvers looked up this page's one-off probe names under
 *              dl.tembrica.com, a zone only our NL DNS server answers
 *              (config/dns-leak.php): the page itself cannot see its resolver,
 *              only that server can. A resolver of the visitor's own provider
 *              while the VPN is on is a DNS leak.
 * Every address is classified by the server from local data only
 * (NetworkClassifier: hosting / relay / tor / isp / private).
 *
 * A leak is an address of a HOME or MOBILE provider that differs from what
 * sites see through the tunnel — never "any address at all".
 *
 *   ConnectionCheck.run({ stun: [...] }) → Promise<report>
 *   ConnectionCheck.analyze(input)       → report   (pure; unit-tested)
 */
(function (root) {
    'use strict';

    var API = '/api/connection-check';
    var ECHO = {
        4: ['https://api.ipify.org?format=json', 'https://v4.ident.me'],
        6: ['https://v6.ident.me']
    };
    var TUNNEL = { hosting: 1, relay: 1, tor: 1 };
    var DNS_ZONE = 'dl.tembrica.com';
    var DNS_API = '/api/dns-leak/';

    /* ---------- addresses ---------- */

    function ipVersion(s) {
        s = String(s || '');
        if (/^\d{1,3}(\.\d{1,3}){3}$/.test(s)) return 4;
        if (/^[0-9a-f:.]+$/i.test(s) && s.indexOf(':') !== -1) return 6;
        return 0;
    }

    /* Full, lower-case form so "2a0d:5600:19:25::11" equals the same address
       written out — WebRTC and the echo services do not agree on notation. */
    function norm(ip) {
        ip = String(ip || '').trim().toLowerCase().replace(/%.*$/, '').replace(/^\[|\]$/g, '');
        if (ipVersion(ip) !== 6) return ip;
        var tail4 = /(\d+\.\d+\.\d+\.\d+)$/.exec(ip);
        if (tail4) {
            var p = tail4[1].split('.').map(Number);
            ip = ip.slice(0, -tail4[1].length) + ((p[0] << 8) | p[1]).toString(16) + ':' + ((p[2] << 8) | p[3]).toString(16);
        }
        var halves = ip.split('::');
        var head = halves[0] ? halves[0].split(':') : [];
        var tail = halves.length > 1 && halves[1] ? halves[1].split(':') : [];
        var parts = head.slice();
        for (var z = halves.length > 1 ? 8 - head.length - tail.length : 0; z > 0; z--) parts.push('0');
        parts = parts.concat(tail);
        return parts.map(function (h) { return ('0000' + h).slice(-4); }).join(':');
    }

    function isPrivate(ip) {
        var v = ipVersion(ip);
        if (v === 4) {
            var p = ip.split('.').map(Number);
            return p[0] === 10 || p[0] === 127 || p[0] === 0 ||
                (p[0] === 172 && p[1] >= 16 && p[1] <= 31) ||
                (p[0] === 192 && p[1] === 168) ||
                (p[0] === 169 && p[1] === 254) ||
                (p[0] === 100 && p[1] >= 64 && p[1] <= 127);          // CGNAT
        }
        if (v === 6) {
            var n = norm(ip);
            var first = parseInt(n.slice(0, 4), 16);
            return n === '0000:0000:0000:0000:0000:0000:0000:0001' ||
                (first & 0xffc0) === 0xfe80 ||                        // link-local
                (first & 0xfe00) === 0xfc00;                          // ULA
        }
        return true;
    }

    /* "candidate:842163049 1 udp 1677729535 203.0.113.7 50000 typ srflx raddr …" */
    function parseCandidate(line) {
        var m = /candidate:\S+\s+\d+\s+(\S+)\s+\d+\s+(\S+)\s+\d+\s+typ\s+(\S+)/i.exec(String(line || ''));
        if (!m) return null;
        var addr = m[2];
        if (/\.local$/i.test(addr)) return { ip: null, type: m[3].toLowerCase(), mdns: true };
        if (!ipVersion(addr)) return null;
        return { ip: addr, type: m[3].toLowerCase(), mdns: false, private: isPrivate(addr), version: ipVersion(addr) };
    }

    /* ---------- gathering ---------- */

    function withTimeout(ms) {
        if (typeof AbortController !== 'function') return { signal: undefined, done: function () {} };
        var c = new AbortController();
        var t = setTimeout(function () { try { c.abort(); } catch (_) {} }, ms);
        return { signal: c.signal, done: function () { clearTimeout(t); } };
    }

    function echo(version, ms) {
        var urls = ECHO[version];
        var i = 0;
        function next() {
            if (i >= urls.length) return Promise.resolve(null);
            var url = urls[i++];
            var to = withTimeout(ms);
            return fetch(url, { cache: 'no-store', credentials: 'omit', signal: to.signal })
                .then(function (r) { return r.ok ? r.text() : ''; })
                .then(function (t) {
                    to.done();
                    t = String(t || '').trim();
                    if (t.charAt(0) === '{') { try { t = JSON.parse(t).ip || ''; } catch (_) { t = ''; } }
                    return ipVersion(t) === version ? t : next();
                })
                .catch(function () { to.done(); return next(); });
        }
        return next();
    }

    function gatherWebRTC(stun, ms) {
        return new Promise(function (resolve) {
            var RTC = root.RTCPeerConnection;
            if (typeof RTC !== 'function') { resolve({ supported: false, blocked: true, candidates: [] }); return; }
            var seen = {}, list = [], done = false, pc;
            function add(line) {
                var c = parseCandidate(line);
                if (!c) return;
                var key = c.mdns ? 'mdns:' + c.type : norm(c.ip) + '|' + c.type;
                if (seen[key]) return;
                seen[key] = 1;
                list.push(c);
            }
            function finish(blocked) {
                if (done) return;
                done = true;
                clearTimeout(timer);
                try {
                    var sdp = pc && pc.localDescription && pc.localDescription.sdp || '';
                    sdp.split(/\r?\n/).forEach(function (l) { if (l.indexOf('a=candidate:') === 0) add(l.slice(2)); });
                } catch (_) {}
                try { pc && pc.close(); } catch (_) {}
                resolve({ supported: true, blocked: !!blocked && list.length === 0, candidates: list });
            }
            var timer = setTimeout(function () { finish(false); }, ms);
            try {
                pc = new RTC({ iceServers: (stun || []).map(function (u) { return { urls: u }; }) });
                pc.createDataChannel('cc');
                pc.onicecandidate = function (e) {
                    if (e && e.candidate && e.candidate.candidate) add(e.candidate.candidate);
                    else if (e && !e.candidate) finish(false);
                };
                pc.onicegatheringstatechange = function () { if (pc.iceGatheringState === 'complete') finish(false); };
                pc.createOffer().then(function (o) { return pc.setLocalDescription(o); })
                    .catch(function () { finish(true); });
            } catch (_) { finish(true); }
        });
    }

    function randomToken(n) {
        var abc = 'abcdefghijklmnopqrstuvwxyz0123456789', out = '';
        var buf = new Uint8Array(n);
        try { root.crypto.getRandomValues(buf); } catch (_) { for (var j = 0; j < n; j++) buf[j] = Math.floor(Math.random() * 256); }
        for (var i = 0; i < n; i++) out += abc.charAt(buf[i] % abc.length);
        return out;
    }

    /* Ask the resolver chain for one-off names nobody else will ever ask for;
       our DNS server answers NXDOMAIN (so the browser connects nowhere) and
       remembers who asked. Polls until resolvers show up, then once more for
       stragglers — one lookup is often asked by several egress addresses. */
    function dnsProbe(opts) {
        opts = opts || {};
        var zone = opts.dnsZone || DNS_ZONE;
        var n = opts.dnsProbes || 3;
        var token = randomToken(16);
        for (var i = 0; i < n; i++) {
            try {
                var img = new root.Image();
                img.referrerPolicy = 'no-referrer';
                img.src = 'https://' + token + '-' + i + '.' + zone + '/p.gif';
            } catch (_) {}
        }
        var waits = opts.dnsWaits || [1500, 1300, 1800];
        var seen = {}, list = [], error = null, tries = 0, found = false;
        return new Promise(function (resolve) {
            function done() { resolve({ token: token, supported: error !== 'unavailable', resolvers: list, error: list.length ? null : error }); }
            function poll() {
                var to = withTimeout(6000);
                fetch(DNS_API + token, { cache: 'no-store', credentials: 'same-origin', signal: to.signal, headers: { Accept: 'application/json' } })
                    .then(function (r) { to.done(); return r.ok ? r.json() : null; })
                    .then(function (d) {
                        if (d && d.error) error = d.error;
                        ((d && d.resolvers) || []).forEach(function (x) {
                            if (x && x.ip && !seen[norm(x.ip)]) { seen[norm(x.ip)] = 1; list.push(x); }
                        });
                    }, function () { to.done(); error = error || 'fetch'; })
                    .then(function () {
                        tries++;
                        if (list.length && found) return done();          // stragglers collected
                        if (list.length) found = true;
                        if (tries >= waits.length || error === 'unavailable') return done();
                        setTimeout(poll, waits[tries]);
                    });
            }
            setTimeout(poll, waits[0]);
        });
    }

    function classify(ips, ms) {
        var to = withTimeout(ms);
        var q = ips.length ? '?ips=' + encodeURIComponent(ips.join(',')) : '';
        return fetch(API + q, { cache: 'no-store', credentials: 'same-origin', signal: to.signal, headers: { Accept: 'application/json' } })
            .then(function (r) { to.done(); if (!r.ok) throw new Error('http ' + r.status); return r.json(); });
    }

    function defaultStun() {
        var list = [];
        try { if (root.__WebRTC && typeof root.__WebRTC.stunUrl === 'function') list.push(root.__WebRTC.stunUrl()); } catch (_) {}
        list.push('stun:stun.l.google.com:19302');
        return list;
    }

    function deviceClock() {
        var tz = '';
        try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) {}
        return { tz: tz, offset: -new Date().getTimezoneOffset() * 60 };
    }

    /* ---------- analysis (pure) ---------- */

    /**
     * input = {
     *   you: {ip, class, asn, org, ...},           — this site's view
     *   echoes: {4: info|null, 6: info|null},      — other sites' view
     *   webrtc: {supported, blocked, candidates:[{ip,type,mdns,private,version}]},
     *   info: {normIp: info},                      — classification of every address
     *   device: {tz, offset}
     * }
     */
    function analyze(input) {
        var you = input.you || null;
        var info = input.info || {};
        var device = input.device || { tz: '', offset: null };
        var echoes = input.echoes || {};
        var wr = input.webrtc || { supported: false, blocked: true, candidates: [] };

        var visible = [];
        var byIp = {};
        function addVisible(x, source) {
            if (!x || !x.ip || x.class === 'private') return;
            var k = norm(x.ip);
            if (byIp[k]) { byIp[k].sources.push(source); return; }
            var v = shallow(x);
            v.sources = [source];
            byIp[k] = v;
            visible.push(v);
        }
        addVisible(you, 'site');
        addVisible(echoes[4], 'echo');
        addVisible(echoes[6], 'echo');

        var tunnel = visible.filter(function (v) { return TUNNEL[v.class]; });
        var tunnelAsn = {};
        tunnel.forEach(function (v) { if (v.asn != null) tunnelAsn[v.asn] = 1; });
        var visibleAsn = {};
        visible.forEach(function (v) { if (v.asn != null) visibleAsn[v.asn] = 1; });

        /* the same family seen as a home provider by THIS site but as a tunnel
           by another one = this site is routed around the VPN */
        var split = !!(you && you.class === 'isp' && tunnel.some(function (v) {
            return v.sources.indexOf('echo') !== -1 && v.version === you.version;
        }));

        /* a whole address family that leaves through a home provider while the
           other family goes through the tunnel */
        var bypass = [];
        if (tunnel.length) {
            visible.forEach(function (v) {
                if (v.class !== 'isp' || tunnelAsn[v.asn]) return;
                var sameFamilyTunnel = tunnel.some(function (t) { return t.version === v.version; });
                if (!sameFamilyTunnel) bypass.push(v);
            });
        }

        var ipOffset = null, ipTz = '';
        var loc = tunnel[0] || you || visible[0] || null;
        if (loc && loc.offset != null) { ipOffset = loc.offset; ipTz = loc.timezone || ''; }
        var tzMatch = ipOffset == null || device.offset == null ? null : Math.abs(ipOffset - device.offset) < 1800;

        var state;
        if (split) state = 'split';
        else if (tunnel.length) state = 'on';
        else if (you && (you.class === 'isp')) state = tzMatch === false ? 'likely' : 'off';
        else state = 'unknown';

        var kind = null;
        if (tunnel.length) {
            var order = ['tor', 'relay', 'hosting'];
            for (var i = 0; i < order.length && !kind; i++) {
                if (tunnel.some(function (t) { return t.class === order[i]; })) kind = order[i];
            }
        }

        /* WebRTC: what each address means next to what sites see */
        var candidates = (wr.candidates || []).map(function (c) {
            var r = shallow(c);
            if (c.mdns) { r.relation = 'hidden'; return r; }
            if (c.private) { r.relation = 'local'; return r; }
            var k = norm(c.ip);
            var ci = info[k] || {};
            r.class = ci.class || 'unknown';
            r.asn = ci.asn != null ? ci.asn : null;
            r.org = ci.org || '';
            r.city = ci.city || '';
            r.country = ci.country || '';
            r.countryCode = ci.countryCode || '';
            if (byIp[k]) r.relation = 'same';
            else if (r.asn != null && visibleAsn[r.asn]) r.relation = 'same_network';
            else if (TUNNEL[r.class]) r.relation = 'other_tunnel';
            else r.relation = 'other';
            return r;
        });
        var protectedState = state === 'on' || state === 'split' || state === 'likely';
        var webrtcLeaks = protectedState ? candidates.filter(function (c) {
            return c.relation === 'other' && c.class === 'isp';
        }) : [];

        /* DNS: who resolved the probe names. A public resolver (Google,
           Cloudflare, Quad9 …) or the VPN's own network is fine; the visitor's
           provider while a VPN is on means the list of sites goes past the
           tunnel. Without a VPN the provider's resolver is simply expected. */
        var dns = null;
        if (input.dns) {
            var resolvers = (input.dns.resolvers || []).map(function (r) {
                var x = shallow(r);
                if (x.resolver) x.relation = 'public';
                else if (x.asn != null && tunnelAsn[x.asn]) x.relation = 'vpn';
                else if (x.class === 'isp') x.relation = 'provider';
                else x.relation = 'other';
                return x;
            });
            dns = {
                supported: input.dns.supported !== false,
                resolvers: resolvers,
                leaks: protectedState ? resolvers.filter(function (x) { return x.relation === 'provider'; }) : [],
                error: input.dns.error || null
            };
        }

        return {
            state: state,                 // on | split | likely | off | unknown
            kind: kind,                   // tor | relay | hosting | null
            you: you,
            visible: visible,
            exit: tunnel[0] || null,
            split: split,
            bypass: bypass,
            webrtc: {
                supported: !!wr.supported,
                blocked: !!wr.blocked,
                candidates: candidates,
                leaks: webrtcLeaks,
                localExposed: candidates.some(function (c) { return c.relation === 'local'; })
            },
            dns: dns,
            timezone: { device: device.tz || '', deviceOffset: device.offset, ip: ipTz, ipOffset: ipOffset, match: tzMatch }
        };
    }

    function shallow(o) { var r = {}; for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) r[k] = o[k]; return r; }

    /* ---------- run ---------- */

    /* opts.webrtc — candidates a tool already gathered with its own STUN
       servers ({supported, blocked, candidates: [parseCandidate(...)]}); the
       report then relates THOSE addresses instead of gathering again.
       opts.dns — true to also run the DNS-leak probe. Opt-in: it costs three
       lookups and up to ~5 s of polling, and only a page that SHOWS the DNS
       result should pay that (the WebRTC monitor calls run() in a loop). */
    function run(opts) {
        opts = opts || {};
        var t0 = Date.now();
        var dnsDone = opts.dns === true ? dnsProbe(opts).catch(function () { return null; }) : Promise.resolve(null);
        return Promise.all([
            echo(4, opts.echoTimeout || 4000),
            echo(6, opts.echoTimeout || 4000),
            opts.webrtc ? Promise.resolve(opts.webrtc) : gatherWebRTC(opts.stun || defaultStun(), opts.webrtcTimeout || 4000)
        ]).then(function (r) {
            var e4 = r[0], e6 = r[1], wr = r[2];
            var ask = [];
            function want(ip) { if (ip && !isPrivate(ip) && ask.indexOf(ip) === -1) ask.push(ip); }
            want(e4); want(e6);
            wr.candidates.forEach(function (c) { if (!c.mdns && !c.private) want(c.ip); });
            return Promise.all([classify(ask.slice(0, 8), opts.apiTimeout || 8000), dnsDone]).then(function (both) {
                var res = both[0], dnsRes = both[1];
                var info = {};
                [res.you].concat(res.others || []).forEach(function (x) { if (x && x.ip) info[norm(x.ip)] = x; });
                var report = analyze({
                    you: res.you,
                    echoes: { 4: e4 ? info[norm(e4)] || null : null, 6: e6 ? info[norm(e6)] || null : null },
                    webrtc: wr,
                    info: info,
                    dns: dnsRes,
                    device: deviceClock()
                });
                report.ms = Date.now() - t0;
                return report;
            });
        });
    }

    var api = {
        run: run,
        analyze: analyze,
        dnsProbe: dnsProbe,
        parseCandidate: parseCandidate,
        normalize: norm,
        isPrivate: isPrivate,
        ipVersion: ipVersion
    };
    root.ConnectionCheck = api;
})(typeof self !== 'undefined' ? self : this);
